import React from 'react';

const Pagination = ({ current = 1, total = 0, pageSize = 10, onChange, showTotal, className = '', ...props }) => {
    const totalPages = Math.ceil(total / pageSize);
    const startItem = (current - 1) * pageSize + 1;
    const endItem = Math.min(current * pageSize, total);

    const handlePageChange = (page) => {
        if (page !== current && page >= 1 && page <= totalPages) {
            onChange?.(page);
        }
    };

    const renderPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, current - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        // First page
        if (startPage > 1) {
            pages.push(
                <button
                    key={1}
                    onClick={() => handlePageChange(1)}
                    className="px-3 py-2 text-sm border border-[#d4d9e3] text-[#101319] hover:bg-[#e9ecf1] rounded"
                >
                    1
                </button>,
            );

            if (startPage > 2) {
                pages.push(
                    <span key="start-ellipsis" className="px-2 py-2 text-[#5b6b8b]">
                        ...
                    </span>,
                );
            }
        }

        // Visible pages
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`px-3 py-2 text-sm border rounded ${
                        i === current
                            ? 'bg-[#e9ecf1] border-[#d4d9e3] text-[#101319] font-medium'
                            : 'border-[#d4d9e3] text-[#101319] hover:bg-[#e9ecf1]'
                    }`}
                >
                    {i}
                </button>,
            );
        }

        // Last page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push(
                    <span key="end-ellipsis" className="px-2 py-2 text-[#5b6b8b]">
                        ...
                    </span>,
                );
            }

            pages.push(
                <button
                    key={totalPages}
                    onClick={() => handlePageChange(totalPages)}
                    className="px-3 py-2 text-sm border border-[#d4d9e3] text-[#101319] hover:bg-[#e9ecf1] rounded"
                >
                    {totalPages}
                </button>,
            );
        }

        return pages;
    };

    if (totalPages <= 1) return null;

    return (
        <div className={`flex items-center justify-center space-x-2 ${className}`}>
            {/* Previous button */}
            <button
                onClick={() => handlePageChange(current - 1)}
                disabled={current === 1}
                className={`px-3 py-2 text-sm border border-[#d4d9e3] rounded ${
                    current === 1
                        ? 'text-[#5b6b8b] cursor-not-allowed opacity-50'
                        : 'text-[#5b6b8b] hover:text-[#101319] hover:bg-[#e9ecf1]'
                }`}
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
            </button>

            {/* Page numbers */}
            {renderPageNumbers()}

            {/* Next button */}
            <button
                onClick={() => handlePageChange(current + 1)}
                disabled={current === totalPages}
                className={`px-3 py-2 text-sm border border-[#d4d9e3] rounded ${
                    current === totalPages
                        ? 'text-[#5b6b8b] cursor-not-allowed opacity-50'
                        : 'text-[#5b6b8b] hover:text-[#101319] hover:bg-[#e9ecf1]'
                }`}
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            </button>

            {/* Total info */}
            {showTotal && <div className="ml-4 text-sm text-[#5b6b8b]">{showTotal(total, [startItem, endItem])}</div>}
        </div>
    );
};

export default Pagination;
